import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type {
  ItemFilter,
  ItemRow,
  Member,
  ObjEntity,
  PageItemRow,
  PrivateTriages,
  SpaceOut,
  UserKind,
  Visibility,
} from "../api/types.gen.ts";
import { type Key, SLOT, t } from "../i18n/index.ts";
import { ATTACHMENTS, triageTarget } from "../lib/attach.ts";
import { patchItem, useEntities } from "../lib/data.ts";
import { upload, uploading, useDropTarget } from "../lib/fileDrop.ts";
import { formatBytes, formatRelative } from "../lib/format.ts";
import { ITEM_JOBS, sayDone, useJobs } from "../lib/jobs.ts";
import { heldUrl, spaceApi, spaceUrl } from "../lib/paths.ts";
import { displayName, nameOf, usePeople } from "../lib/people.ts";
import { useRememberedQuery } from "../lib/remember.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { atLeast, canEdit } from "../lib/roles.ts";
import { location, navigate, setQuery, tabHref, tabParam } from "../lib/router.ts";
import { SELECTED_ROW, type Selection, useSelection } from "../lib/selection.ts";
import { me, mySpaces, type PageProps, setShowPrivate, showsPrivate } from "../lib/state.ts";
import { titleOf } from "../lib/titles.ts";
import { toast } from "../lib/toast.ts";
import { useId } from "../lib/uid.ts";
import { isPrivate } from "../lib/visibility.ts";
import { Button, Link } from "../ui/Button.tsx";
import { Chip } from "../ui/Chip.tsx";
import { ItemCommentCount } from "../ui/counts.tsx";
import { ConfirmDialog, Dialog } from "../ui/Dialog.tsx";
import { Dropzone } from "../ui/Dropzone.tsx";
import { EntityGlyph } from "../ui/EntityGlyph.tsx";
import { Select } from "../ui/form.tsx";
import { AddLinkIcon, ChooserIcon, CloseIcon, FolderIcon, MoreIcon, OrgIcon, PrivateIcon } from "../ui/icons.tsx";
import { JobProgress } from "../ui/jobProgress.tsx";
import { Menu, type MenuEntry, type MenuItem } from "../ui/Menu.tsx";
import { Async, CopyButton, EmptyState, Slotted } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { SelectBox, SelectionRow } from "../ui/selection.tsx";
import { buttonClass, iconButtonClass, LINK } from "../ui/styles.ts";
import { TabNav } from "../ui/TabNav.tsx";
import { Toolbar } from "../ui/Toolbar.tsx";
import { TriageIcon } from "../ui/TriageIcon.tsx";
import { MoveDialog } from "./folders/move.tsx";
import { HeldPanel, type HeldScope } from "./Held.tsx";
import { VisibilityMark } from "./item/visibility.tsx";
import { AttachForm } from "./items/attach.tsx";
import { BulkActions } from "./items/bulk.tsx";
import { useCreateItem } from "./items/create.tsx";
import { EntityLabel } from "./items/entityLabel.tsx";
import { AttachmentCount, PeekKindIcon } from "./items/icons.tsx";

const TRIAGE_LIMIT = 200;

/** My private triage area (admins: or someone else's, or everyone's), or the space's shared one. */
type Tab = "mine" | "shared";
/** Each tab's mail from unknown senders (held apart until it's let in): what was sent to whose triage email address. */
const HELD: Record<Tab, HeldScope> = { mine: "personal", shared: "shared" };
const HINT: Record<Tab, Key> = { mine: "triage.mineHint", shared: "triage.sharedHint" };
/** Under Private, every member's private items (admins seeing them), not one member's. */
const EVERYONE = "everyone";

/**
 * What the page shows: a tab, and under Private whose private items: yours (`me`), a member's (their id) or
 * everyone's (admins, while they see other members' private items).
 */
interface View {
  tab: Tab;
  whose: string;
}

/** The view is your own triage area (yours, or the organization's): where new files go and your mail waits. */
const own = (v: View): boolean => v.tab === "shared" || v.whose === "me";

/** What the view lists: the organization's items, your private ones, a member's, or every private one you see. */
function filterOf(v: View): ItemFilter {
  if (v.tab === "shared") {
    return { triage: true, private_to: "none" };
  }
  return v.whose === EVERYONE
    ? { triage: true, visibility: "private" }
    : { triage: true, private_to: v.whose === "me" ? "me" : v.whose };
}

interface RowEnv {
  space: SpaceOut;
  /** Rows say whose private items they are (someone else's, or everyone's, listed). */
  others: boolean;
  members: readonly Member[] | undefined;
  entities: readonly ObjEntity[];
  onChange: () => void;
  /** Rows can be selected (editors) to act on together. */
  sel?: Selection | undefined;
}

function FileInto({
  env,
  row,
  opener,
}: {
  env: RowEnv;
  row: ItemRow;
  /** Set to what opens its dialog, for opening it from elsewhere (the row's menu). */
  opener: { current: (() => void) | null };
}) {
  const [open, setOpen] = useState(false);
  opener.current = () => setOpen(true);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <AddLinkIcon size={14} />
        {t("triage.fileInto")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t("triage.fileTitle", { title: row.title ?? "" })}>
        <AttachForm
          space={env.space}
          ids={[row.id]}
          entity={row.entity ?? null}
          entities={env.entities}
          onClose={() => setOpen(false)}
          onDone={(to) => {
            // Filing a private item into an organization one makes it the organization's (its list kept).
            toast(t(isPrivate(row) ? "triage.filedPublic" : "triage.filed", { target: to.label }));
            env.onChange();
          }}
        />
      </Dialog>
    </>
  );
}

/** What it can be: the space's entities (or none), as menu items setting it (busy while one's being set). */
function useTypes(env: RowEnv, row: ItemRow): { types: MenuEntry[]; busy: boolean } {
  const [run, busy] = useAction(async (entity: ObjEntity | null) => {
    await patchItem(env.space, row, { entity: entity?.id ?? null });
    toast(entity ? t("triage.typed", { entity: entity.name }) : t("triage.untyped"));
    env.onChange();
  });
  return {
    busy,
    types: [
      ...env.entities.map((e) => ({
        key: e.id,
        label: e.name,
        icon: <EntityGlyph icon={e.icon} empty="dot" />,
        current: e.id === row.entity,
        onSelect: () => run(e),
      })),
      {
        key: "none",
        label: t("items.noEntity"),
        icon: <EntityGlyph icon={null} empty="none" />,
        current: !row.entity,
        onSelect: () => run(null),
      },
    ],
  };
}

/** What it is: the space's entities (or none), set from here without opening it. */
function TypeMenu({ env, row, types, busy }: { env: RowEnv; row: ItemRow; types: MenuEntry[]; busy: boolean }) {
  const current = env.entities.find((e) => e.id === row.entity);
  return (
    <Menu
      label={
        <>
          {current ? <EntityGlyph icon={current.icon} /> : null}
          <span>{current ? current.name : t("triage.setType")}</span>
          <ChooserIcon />
        </>
      }
      class={`${buttonClass("secondary", "sm")} ${busy ? "opacity-50" : ""}`}
      items={types}
    />
  );
}

/** The rest, out of the way: who sees it (the organization, or private), delete; first, what doesn't fit beside it. */
function More({ env, row, overflow }: { env: RowEnv; row: ItemRow; overflow: readonly MenuItem[] }) {
  const [confirm, setConfirm] = useState(false);
  const [moving, setMoving] = useState(false);
  const [share, sharing] = useAction(async (visibility: Visibility) => {
    await patchItem(env.space, row, { visibility });
    toast(t(visibility === "private" ? "visibility.madePrivate" : "visibility.madeOrg"));
    env.onChange();
  });
  const priv = isPrivate(row);
  const [remove, removing] = useAction(async () => {
    await api.del(spaceApi(env.space, `/items/${row.id}`));
    env.onChange();
  });
  return (
    <>
      <Menu
        label={
          <>
            <MoreIcon />
            <span class="sr-only">{t("item.more")}</span>
          </>
        }
        class={`${iconButtonClass("sm", "secondary")} ${sharing || removing ? "opacity-50" : ""}`}
        items={[
          ...overflow,
          {
            key: "org",
            group: t("visibility.label"),
            label: t("visibility.org"),
            icon: <OrgIcon />,
            current: !priv,
            onSelect: () => (priv ? share("org") : undefined),
          },
          {
            key: "private",
            group: t("visibility.label"),
            label: t("visibility.privateChoice"),
            icon: <PrivateIcon />,
            current: priv,
            onSelect: () => (priv ? undefined : share("private")),
          },
          { label: t("folders.moveTo"), icon: <FolderIcon />, onSelect: () => setMoving(true), separated: true },
          { label: t("common.delete"), onSelect: () => setConfirm(true), danger: true },
        ]}
      />
      <MoveDialog
        space={env.space}
        open={moving}
        title={t("folders.moveTitle", { name: titleOf(row) })}
        moving={{ kind: "items", ids: [row.id], items: [row], triage: true }}
        onClose={() => setMoving(false)}
        onDone={env.onChange}
      />
      <ConfirmDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t("triage.deleteTitle")}
        message={t("triage.deleteMessage", { title: row.title ?? "" })}
        confirmLabel={t("common.delete")}
        onConfirm={remove}
      />
    </>
  );
}

/** Out of triage, without filing it anywhere. */
function TakeOut({ env, row }: { env: RowEnv; row: ItemRow }) {
  const [run, busy] = useAction(async () => {
    await patchItem(env.space, row, { triage: false });
    toast(t("triage.takenOut"));
    env.onChange();
  });
  return (
    <Button size="sm" busy={busy} onClick={() => run()}>
      <TriageIcon size={14} class="sm:hidden" />
      {t("triage.takeOut")}
    </Button>
  );
}

/**
 * What it is, where it goes (or out), and the rest under "More", on one line: where there's no room for them all (a
 * small phone), filing and then what it is go into "More" (taking it out stays).
 */
function Actions({ env, row }: { env: RowEnv; row: ItemRow }) {
  const filing = useRef<(() => void) | null>(null);
  const { types, busy } = useTypes(env, row);
  return (
    <Toolbar
      hug={true}
      items={[
        {
          key: "type",
          node: <TypeMenu env={env} row={row} types={types} busy={busy} />,
          drop: 10,
          menu: types.map((e) => ({ ...e, group: t("triage.setType") })),
        },
        {
          key: "file",
          node: <FileInto env={env} row={row} opener={filing} />,
          drop: 20,
          menu: [{ label: t("triage.fileInto"), onSelect: () => filing.current?.() }],
        },
        { key: "takeOut", node: <TakeOut env={env} row={row} /> },
      ]}
      menu={(overflow) => <More env={env} row={row} overflow={overflow} />}
    />
  );
}

function Row({ env, row }: { env: RowEnv; row: ItemRow }) {
  const meta = [row.file?.mime, row.file ? formatBytes(row.file.size) : null, formatRelative(row.created_at)];
  const entity = env.entities.find((e) => e.id === row.entity);
  // Wide, one line: what it is, then the actions at its end. Narrow (a phone, beside a pinned look), the title has the
  // row's width and the actions a line of their own under it.
  return (
    <li class={`flex items-start gap-3 px-4 py-3 @3xl/main:items-center ${env.sel?.has(row.id) ? SELECTED_ROW : ""}`}>
      {env.sel ? <SelectBox sel={env.sel} id={row.id} title={titleOf(row)} /> : null}
      <PeekKindIcon space={env.space} item={row} />
      <div class="flex min-w-0 flex-1 flex-col gap-2 @3xl/main:flex-row @3xl/main:items-center @3xl/main:gap-3">
        <RowText env={env} row={row} meta={meta} entity={entity} />
        {canEdit(env.space) ? <Actions env={env} row={row} /> : null}
      </div>
    </li>
  );
}

/** A row's title (and what's beside it) and its line of facts. */
function RowText({
  env,
  row,
  meta,
  entity,
}: {
  env: RowEnv;
  row: ItemRow;
  meta: readonly (string | null | undefined)[];
  entity: ObjEntity | undefined;
}) {
  return (
    <div class="min-w-0 flex-1">
      {/* One run of text: what's after the title follows its last word (a title's own line otherwise, the comment
          button, hidden until hovered, could leave a blank line under a long one). */}
      <p class="[&>*+*]:ml-2 [&>*+*]:inline-flex [&>*+*]:align-middle">
        <Link href={spaceUrl(env.space, `/items/${row.id}`)} class={`${LINK} [overflow-wrap:anywhere]`}>
          {titleOf(row)}
        </Link>
        {/* Public ones on any tab; private ones where the view doesn't say whose already. */}
        {env.others || row.visibility === "public" ? <VisibilityMark item={row} members={env.members} /> : null}
        <ItemCommentCount space={env.space} id={row.id} />
        <AttachmentCount n={row.refs?.[ATTACHMENTS]?.length ?? 0} />
        {entity ? <EntityLabel entity={entity} /> : null}
      </p>
      <p class="text-xs text-zinc-500">{meta.filter(Boolean).join(" · ")}</p>
    </div>
  );
}

function Tabs({ space, tab }: { space: SpaceOut; tab: Tab }) {
  return (
    <TabNav
      label={t("triage.tabs")}
      tabs={(["mine", "shared"] as const).map((x) => ({
        href: tabHref(spaceUrl(space, "/triage"), x),
        label: t(`triage.tab.${x}`),
        current: x === tab,
      }))}
    />
  );
}

/** How much mail from unknown senders waits for you in a tab's triage area (members never see the space's). */
function heldCount(space: SpaceOut, tab: Tab): number {
  const { held, held_personal: personal } = space.counts;
  return { mine: personal, shared: held - personal }[tab];
}

/** Unknown senders, a filter of the tab: there (with how much waits) only while some does, or while it's on. */
function HeldFilter({ space, tab, on }: { space: SpaceOut; tab: Tab; on: boolean }) {
  const n = heldCount(space, tab);
  if (n === 0 && !on) {
    return null;
  }
  const base = tabHref(spaceUrl(space, "/triage"), tab);
  return (
    <div class="mb-4 flex items-center gap-2">
      <Chip
        size="md"
        tone={on ? "blue" : "gray"}
        href={on ? base : `${base}&held=1`}
        title={t(on ? "triage.heldOff" : "triage.heldOn")}
      >
        <span class="inline-flex items-center gap-1">
          {n > 0 ? t("triage.heldCount", { count: n }) : t("triage.held")}
          {on ? <CloseIcon size={12} /> : null}
        </span>
      </Chip>
    </div>
  );
}

/** The old addresses of Unknown senders (its page, then the triage area's tab): now a filter of the tab it waits in. */
export function HeldRedirect({ space }: Pick<PageProps, "space">) {
  useEffect(() => navigate(heldUrl(space), true), [space]);
  return null;
}

/** Where mail for this view goes (my personal address, or the space's), and who sees it; or whose items these are. */
function Address({ space, view }: { space: SpaceOut; view: View }) {
  const members = usePeople();
  const mine = mySpaces().find((s) => s.space === space.id)?.triage_email_address;
  const address = own(view) ? { mine, shared: space.triage_email_address }[view.tab] : null;
  const hint = own(view)
    ? t(HINT[view.tab])
    : view.whose === EVERYONE
      ? t("triage.everyoneHint")
      : t("triage.memberHint", { name: nameOf(members, view.whose) });
  return (
    <div class="mb-4 flex flex-col gap-1 text-sm text-zinc-600">
      {address ? (
        <p>
          <Slotted text={t("triage.sendTo", { address: SLOT })}>
            <code class="rounded bg-zinc-100 px-1.5 py-0.5">{address}</code>
          </Slotted>{" "}
          <CopyButton text={address} label={t("common.copy")} />
        </p>
      ) : null}
      <p>{hint}</p>
    </div>
  );
}

/** The triage area is one screen (its tab and member are what it remembers). */
const NO_KEYS: readonly string[] = [];

/**
 * The old All tab (everything unfiled an admin saw, or one member's): now Private, everyone's or that member's; its
 * Unknown senders, the tab they wait in.
 */
function AllRedirect({ space }: Pick<PageProps, "space">) {
  const asked = location.value.searchParams;
  const to =
    asked.get("held") === "1"
      ? heldUrl(space)
      : `${spaceUrl(space, "/triage")}?member=${encodeURIComponent(asked.get("member") ?? EVERYONE)}`;
  useEffect(() => navigate(to, true), [to]);
  return null;
}

/**
 * Whose private items Private shows: someone else's (or everyone's) only for admins seeing other members' private
 * items; turning that off brings it back to yours.
 */
function whoseOf(space: SpaceOut): string {
  const asked = location.value.searchParams.get("member");
  return asked && atLeast(space.role, "admin") && showsPrivate(space) ? asked : "me";
}

/** Someone's name, and what they are when they aren't a person; with how much waits for them, when that's known. */
const pickLabel = (name: string, kind: UserKind, count?: number): string =>
  `${name}${kind === "human" ? "" : ` (${t(`userKind.${kind}`)})`}${count === undefined ? "" : ` · ${count}`}`;

/**
 * Whose private triage area (admins): yours, someone's with something waiting (and how much), or everyone's.
 * Another's is another member's private items, so picking one turns on seeing them; until it's on, others are listed
 * by name (how much waits for them isn't yours to see yet).
 */
function WhosePicker({ space, whose }: { space: SpaceOut; whose: string }) {
  const counts = useResource(`triage-private:${space.id}`, (signal) =>
    api.get<PrivateTriages>(spaceApi(space, "/triage/private"), undefined, signal),
  );
  const members = usePeople();
  const myId = me.value?.id ?? "";
  const rows = counts.data?.data ?? [];
  const shown = showsPrivate(space);
  const others = shown
    ? rows
        .filter((r) => r.user !== myId)
        .map((r) => ({ value: r.user, label: pickLabel(displayName(r.user, r.name), r.kind, r.count) }))
    : (members ?? [])
        .filter((m) => m.id !== myId)
        .map((m) => ({ value: m.id, label: pickLabel(displayName(m.id, m.name), m.kind) }));
  // Someone picked with nothing waiting (they're not among those counted): still there, with none.
  const picked = members?.find((m) => m.id === whose);
  if (picked && !others.some((o) => o.value === whose)) {
    others.push({ value: picked.id, label: pickLabel(displayName(picked.id, picked.name), picked.kind, 0) });
  }
  const [pick, busy] = useAction(async (v: string) => {
    if (v !== "me" && !showsPrivate(space)) {
      await setShowPrivate(space, true);
    }
    setQuery({ member: v === "me" ? null : v });
  });
  const id = useId();
  return (
    <div class="flex max-w-full min-w-0 items-center gap-2">
      <label for={id} class="text-sm text-zinc-600">
        {t("triage.whose")}
      </label>
      <div class="w-80 min-w-0 max-w-full shrink">
        <Select
          id={id}
          value={whose}
          disabled={busy}
          onValue={(v) => pick(v)}
          options={[
            { value: "me", label: pickLabel(t("triage.you"), "human", rows.find((r) => r.user === myId)?.count ?? 0) },
            ...others,
            { value: EVERYONE, label: t("triage.everyone") },
          ]}
        />
      </div>
    </div>
  );
}

export function Triage({ space }: PageProps) {
  useRememberedQuery(`triage.${space.id}`, NO_KEYS);
  const asked = location.value.searchParams;
  const whose = whoseOf(space);
  const entities = useEntities(space);
  // Seeing others' private items turned off (here or anywhere): back to yours.
  const stale = asked.has("member") && whose === "me";
  useEffect(() => {
    if (stale) {
      setQuery({ member: null });
    }
  }, [stale]);
  const tab = tabParam<Tab>(["mine", "shared"]);
  const view: View = { tab, whose: tab === "mine" ? whose : "me" };
  const create = useCreateItem({ space, entities: entities.data ?? [], triage: { mine: tab === "mine" } });
  if (asked.get("tab") === "held") {
    return <HeldRedirect space={space} />;
  }
  if (asked.get("tab") === "all") {
    return <AllRedirect space={space} />;
  }
  // Mail from unknown senders is only ever yours (or, admins, the organization's) to see.
  const held = own(view) && asked.get("held") === "1";
  return (
    <>
      {/* New items go into the triage area shown: yours or the organization's, not someone else's. */}
      <PageHeader title={t("triage.title")} buttons={canEdit(space) && own(view) ? [create.action] : []}>
        {tab === "mine" && atLeast(space.role, "admin") ? <WhosePicker space={space} whose={view.whose} /> : null}
      </PageHeader>
      {create.dialogs}
      {space.counts.forwarding > 0 && !held ? (
        <p role="status" class="mb-4 rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-950">
          {t("triage.forwardingNotice")}{" "}
          <Link href={heldUrl(space)} class={LINK}>
            {t("triage.forwardingLink")}
          </Link>
        </p>
      ) : null}
      <Tabs space={space} tab={tab} />
      {own(view) ? <HeldFilter space={space} tab={tab} on={held} /> : null}
      {held ? <HeldPanel space={space} scope={HELD[tab]} /> : <Items space={space} view={view} />}
    </>
  );
}

/** A view of the triage area's items. */
function Items({ space, view }: { space: SpaceOut; view: View }) {
  const members = usePeople();
  // What the view lists (and every item of it, for a bulk action on all of them).
  const filter = filterOf(view);
  const key = `${space.id}:${view.tab}:${view.whose}`;
  const list = useResource(`triage:${key}`, (signal) =>
    api.get<PageItemRow>(spaceApi(space, "/items"), { ...filter, limit: TRIAGE_LIMIT }, signal),
  );
  // Files dropped anywhere on the page (or on its drop zone, or picked) go to this tab's triage area: yours or the
  // organization's, not someone else's.
  const mine = view.tab === "mine";
  const drop = canEdit(space) && own(view) ? triageTarget(space, { mine, level: 1, done: list.reload }) : null;
  useDropTarget(drop);
  const entities = useEntities(space);
  const sel = useSelection(key);
  const jobs = useJobs(space, ITEM_JOBS, (job) => {
    sayDone(job);
    list.reload();
  });
  const env: RowEnv = {
    space,
    others: !own(view),
    members,
    entities: entities.data ?? [],
    onChange: list.reload,
    sel: canEdit(space) ? sel : undefined,
  };
  return (
    <>
      <Address space={space} view={view} />
      {drop ? (
        <div class="mb-6">
          <Dropzone
            label={uploading(drop.key) ? drop.busy : t("triage.drop")}
            hint={t("triage.dropHint")}
            disabled={uploading(drop.key)}
            onFiles={(files) => upload(drop, files)}
          />
        </div>
      ) : null}
      <Async r={list}>
        {(page) =>
          page.data.length === 0 ? (
            <EmptyState title={t("triage.empty")}>{t("triage.emptyHint")}</EmptyState>
          ) : (
            <>
              {env.sel ? (
                <SelectionRow
                  sel={env.sel}
                  job={jobs.running ? <JobProgress job={jobs.running} /> : null}
                  shown={{ ids: page.data.map((r) => r.id), total: page.total ?? page.data.length, filtered: false }}
                >
                  {(bar) =>
                    env.sel ? (
                      <BulkActions
                        space={space}
                        sel={env.sel}
                        actions={["archive", "entity", "rename", "move", "file", "delete"]}
                        entities={env.entities}
                        query={{ filter, total: page.total ?? page.data.length }}
                        rows={page.data}
                        triage={true}
                        onDone={list.reload}
                        onJob={jobs.follow}
                        bar={bar}
                      />
                    ) : null
                  }
                </SelectionRow>
              ) : null}
              <ul class="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
                {page.data.map((row) => (
                  <Row key={row.id} env={env} row={row} />
                ))}
              </ul>
            </>
          )
        }
      </Async>
    </>
  );
}
